import CloseButton from "../buttons/CloseButton";
import { memo, useEffect, useState } from "react";
import FormGestor from "../formularios/FormGestor.jsx";
import TextButton from "../buttons/TextButton.jsx";
import { formatearCampoFirestore } from "../../functions/dataFunctions";
import { eventos } from "../formularios/data/FormContent.js";
import { fichaContent } from "./data/FichaContent.js";
import ChipUsuario from "../funcionales/ChipUsuario.jsx";
import DondeEsta from "../funcionales/DondeEsta.jsx";
import Telefono from "../funcionales/Telefono.jsx";
import Patente from "../funcionales/Patente.jsx";
import PerfilUsuario from "./PerfilUsuario.jsx";
import { Link, useOutletContext } from "react-router-dom";
import { rutaDePersona } from "../../shared/personas";
import { ruta as rutaDeUnidad, tipoDeColeccion } from "../../shared/unidad";
import { polizaDeUnidad } from "../../services/seguros";
import { lineaSeguro, puedeVerSeguro } from "../../shared/seguroFicha";
import "./css/Fichas.css";

/* La salida a la ficha completa.
 *
 * Sólo para los tres padrones que tienen una: tractores, furgones y personas.
 * A `stock` o `proveedores` no se les inventa un enlace — uno que no lleva a
 * ningún lado es peor que no ofrecerlo.
 *
 * ES LA MISMA FALLA QUE YA TUVIMOS TRES VECES: el camino existía y desde el
 * padrón, que es donde alguien está mirando una unidad o una persona concreta,
 * la única forma de llegar era escribir el número en el buscador. Un botón que
 * no está donde se hace el trabajo es lo mismo que no estar.
 */
const AFicha = ({ coleccion, elemento }) => {
  const tipo = tipoDeColeccion(coleccion);
  /* El id de tractores y furgones ES el interno; el de personas, el DNI. Sin
   * número no hay ficha: un enlace a `/unidad/tractor/` no lleva a ningún
   * lado. */
  const destino = tipo
    ? rutaDeUnidad(tipo, elemento?.id)
    : coleccion === "personas"
      ? rutaDePersona(elemento?.dni ?? elemento?.id)
      : null;
  if (!destino) return null;
  return (
    <Link className="ficha-a-unidad" to={destino}>
      {tipo
        ? "Ver la ficha completa de la unidad →"
        : "Ver la ficha completa de la persona →"}
    </Link>
  );
};

/* LA PÓLIZA, ARRIBA DE TODO (Aldo, Tráfico, 23/10: «no encuentro dónde está
 * VER LA FICHA COMPLETA; necesito que se vea ni bien hago la consulta»).
 *
 * Lo que se abre al buscar un tractor o un furgón es esta ficha, no la página
 * de la unidad: la línea «Seguro» tiene que estar acá. Es LA MISMA línea que en
 * `Unidad.jsx` —mismo `polizaDeUnidad`, mismo `lineaSeguro`, mismo permiso
 * (`puedeVerSeguro`)—, no una segunda versión de las frases.
 *
 * No frena la ficha: se pide aparte y aparece cuando llega. Si no se pudo
 * leer, no va —«no hay póliza» sería contestar algo que no se sabe—. Sin
 * dominio tampoco: no hay con qué buscarla. */
const SeguroRapido = ({ dominio }) => {
  const [poliza, setPoliza] = useState(undefined);
  useEffect(() => {
    let vivo = true;
    setPoliza(undefined);
    polizaDeUnidad(dominio)
      .then((p) => vivo && setPoliza(p ?? null))
      .catch(() => vivo && setPoliza(undefined));
    return () => {
      vivo = false;
    };
  }, [dominio]);
  const seguro = lineaSeguro(poliza);
  if (!seguro) return null;
  return (
    <div
      className={`ficha-seguro${seguro.aviso ? " ficha-seguro-aviso" : ""}`}
      data-testid="ficha-seguro"
    >
      <strong>Seguro</strong>
      <span className={seguro.neutro ? "tenue" : undefined}>{seguro.texto}</span>
    </div>
  );
};

const Ficha = ({
  elemento,
  coleccion = null,
  area = null,
  filtroParaForm = null,
  onRender,
  container = Object.keys(elemento),
  reload = null,
  editable = true,
  onClose,
}) => {
  const titulado = container.find((campo) => campo.type === "title");
  const tituladoAbajo = container.find((campo) => campo.type === "secondtitle");
  const titulo = titulado ? elemento[titulado.key] : elemento["id"];
  const tituloAbajo = tituladoAbajo ? elemento[tituladoAbajo.key] : null;
  /* EDITAR NO ABRE OTRO POPUP: la ficha se pone editable.
   *
   * Lo pidió Igna mirando el padrón: *«al poner Editar no aparezca ese popup,
   * son clicks extra; que los campos pasen a ser editables y se pueda
   * modificar de ahí directamente»*. Antes eran DOS tarjetas apiladas —la
   * ficha abierta y encima el formulario, con su propio título y su propia
   * cruz— para tocar los mismos datos que ya estaban a la vista.
   *
   * El formulario es el MISMO (`FormGestor`): no se reescribe una segunda
   * pantalla de edición. Lo único que cambia es dónde se dibuja, y eso lo
   * resuelve `.ficha-edicion` en el CSS — así vale para los seis padrones y
   * para los eventos de una sola vez, en vez de tocar trece formularios. */
  const [editando, setEditando] = useState(false);
  /* Los permisos llegan por el contexto del layout, como en `Unidad.jsx`;
   * fuera de él (`null`) se muestra, igual que el resto del hub. */
  const { permisos = null } = useOutletContext() || {};
  const conSeguro =
    !!tipoDeColeccion(coleccion) &&
    !!elemento?.dominio &&
    puedeVerSeguro(permisos);
  const eventosPorteria = eventos.porteria;

  const auxCampos = coleccion || area || "eventos";

  /* De qué formulario es este evento. Las llaves tienen el suyo —una entrega
   * no se parece a un cruce del portón— y una entrada de portería con vehículo
   * particular usa la variante. Sale del propio evento y no de la pantalla:
   * la ficha se abre desde varios lados y el área es del dato. */
  const esEvento = auxCampos === "eventos";
  const filtroDelEvento =
    elemento?.tipo === "ENTREGA" || elemento?.tipo === "RETIRA"
      ? "llaves"
      : null;
  const subFiltroDelEvento =
    elemento?.vehiculo && elemento?.area ? `${elemento.area}Particular` : null;

  const campos = fichaContent[auxCampos] || [];

  const bloquePrincipal = campos.filter(
    (campo) =>
      campo.type === "principal" &&
      elemento[campo.key] !== undefined &&
      elemento[campo.key] !== null &&
      elemento[campo.key] !== "",
  );

  const bloqueSecundario = campos.filter(
    (campo) =>
      campo.type === "secondary" &&
      elemento[campo.key] !== undefined &&
      elemento[campo.key] !== null &&
      elemento[campo.key] !== "",
  );

  const bloqueBool = campos.filter(
    (campo) =>
      campo.type === "boolean" &&
      elemento[campo.key] !== undefined &&
      elemento[campo.key] !== null &&
      elemento[campo.key] !== "",
  );

  if (!elemento || typeof elemento !== "object") {
    console.log("[Error] Ficha espera recibir un objeto elemento");
    return null;
  }
  console.log(`----------- Render Ficha ${elemento.id}`);

  /* GUARDAR CIERRA LA FICHA Y CANCELAR NO, y no es una asimetría de estilo.
   *
   * La ficha dibuja el `elemento` que recibió al abrirse. Después de guardar
   * ese objeto es el VIEJO: volver a la vista de lectura mostraría los valores
   * de antes justo arriba del cambio que se acaba de hacer, que es la peor
   * forma de contestar «se guardó». Cancelando no cambió nada, así que ahí sí
   * se puede volver — y volver es lo correcto: cerrar la ficha entera porque
   * alguien se arrepintió de tocar un campo es hacerle perder el lugar. */
  const handleClose = async () => {
    if (reload) await reload();
    setEditando(false);
    onClose();
  };

  const volverALaFicha = () => setEditando(false);

  return (
    <div className="ficha">
      <div className="ficha-content">
        <CloseButton onClose={onClose} />
        <h1 className="ficha-header">
          <strong className="ficha-id">{titulo}</strong>
          {tituladoAbajo && <span className="nombres">{tituloAbajo}</span>}{" "}
        </h1>
        <hr />

        {/* LA EDICIÓN, ADENTRO DE LA MISMA TARJETA.
         *
         * `FormGestor` es el de siempre y sigue dibujando su propio envoltorio
         * de modal; `.ficha-edicion` lo desarma por CSS. Se hace así y no
         * agregándole un modo «en línea» a cada formulario porque son TRECE
         * —seis padrones, seis eventos y el de reserva— y el que no se toque
         * queda abriendo el popup viejo sin que nada falle: la pantalla
         * andaría, con dos formas distintas de editar según dónde entraste.
         *
         * Y reemplaza a la vista de lectura en vez de sumarse abajo: los
         * mismos datos dos veces —uno en gris y otro en una casilla— obligan a
         * mirar cuál de los dos es el que se está por guardar. */}
        {editando && (
          <div className="ficha-edicion">
            <p className="ficha-editando">
              Editando. Nada cambia hasta apretar Guardar.
            </p>

            <FormGestor
              filtroParaForm={filtroParaForm}
              elemento={elemento}
              tipo={auxCampos}
              /* EL EDITAR DE UN EVENTO ABRÍA EN BLANCO.
               *
               * `FormEvents` busca los campos con
               * `eventos[subFiltro || filtro || coleccion]` y acá no se le
               * pasaba ninguno de los tres: `campos` quedaba en `[]` y el
               * modal se dibujaba sin un solo renglón, con su botón de
               * guardar.
               *
               * La derivación NO es nueva: estaba escrita en el bloque de
               * abajo que alguien comentó al unificar los formularios, y se
               * perdió en la mudanza. El área del evento es la que elige el
               * formulario —portería, tractores, furgones, tráfico—, las
               * llaves tienen el suyo, y una entrada de portería con vehículo
               * particular usa una variante. */
              coleccion={esEvento ? elemento?.area : coleccion}
              filtro={esEvento ? filtroDelEvento : null}
              subFiltro={esEvento ? subFiltroDelEvento : null}
              onGuardar={handleClose}
              onClose={volverALaFicha}
            />

            {/* La salida SIN guardar tiene que estar escrita.
             *
             * La cruz del formulario se esconde —arriba ya está la de la
             * ficha, y dos en la misma esquina no se sabe cuál cierra qué—,
             * así que sin este botón la única forma de arrepentirse sería
             * cerrar la ficha entera o apretar Esc, que no está anunciado en
             * ningún lado. */}
            <div className="ficha-buttons">
              <TextButton text={"Cancelar"} onClick={volverALaFicha} />
            </div>
          </div>
        )}

        {!editando && (
          <>
            {conSeguro && <SeguroRapido dominio={elemento.dominio} />}
            <AFicha coleccion={coleccion} elemento={elemento} />
            {area === "user" && <PerfilUsuario />}
            <div className="ficha-subheader">
              {campos?.map((campo, index) => {
                const valor = elemento[campo.key];

                if (!valor || campo.type !== "subtitle") return null;

                return (
                  <span key={campo.key || index}>
                    {formatearCampoFirestore(valor)}
                  </span>
                );
              })}
            </div>

            {bloquePrincipal.length > 0 && (
              <>
                <label>
                  <strong className="ficha-info-title">Información</strong>
                </label>
                <div className="ficha-info-box">
                  {campos.map((campo, index) => {
                    const valor = elemento[campo.key];

                    if (!valor || campo.type !== "principal") return null;

                    return (
                      <div className="ficha-info" key={campo.key || index}>
                        <strong>{campo.label}</strong>
                        {/* El dominio, como chapa Mercosur: es el mismo dato
                            que en la tabla de al lado y se tiene que ver igual
                            (Igna, 03/10: «quedan re facheras»). */}
                        <span>
                          {campo.key === "dominio" ? (
                            <Patente dominio={formatearCampoFirestore(valor)} />
                          ) : (
                            formatearCampoFirestore(valor).toUpperCase()
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {bloqueSecundario.length > 0 && (
              <>
                <label>
                  <strong className="ficha-info-title">Información</strong>
                </label>
                <div className="ficha-info-box">
                  {campos.map((campo, index) => {
                    const valor = elemento[campo.key];

                    if (!valor || campo.type !== "secondary") return null;

                    return (
                      <div className="ficha-info" key={campo.key || index}>
                        <strong>{campo.label}</strong>
                        <span>
                          {formatearCampoFirestore(valor).toUpperCase()}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {bloqueBool.length > 0 && (
              <>
                <label>
                  <strong className="ficha-info-title">Documentación</strong>
                </label>
                <div className="ficha-chips-box">
                  {campos.map((campo, index) => {
                    const valor = elemento[campo.key];

                    if (!valor || campo.type !== "boolean") return null;

                    return (
                      <span
                        key={campo.key}
                        className={`chequeo-item ${
                          valor ? "chequeo-ok" : "chequeo-fail"
                        }`}
                      >
                        {campo.label}
                      </span>
                    );
                  })}
                </div>
              </>
            )}

            {elemento.chequeos && (
              <>
                <label>
                  <strong className="ficha-info-title">Chequeos</strong>
                </label>
                <div className="checkbox-list">
                  {eventosPorteria
                    .find((campo) => campo.key === "chequeos")
                    ?.items.map((item) => {
                      const valor = elemento.chequeos?.[item.key];

                      return (
                        <span
                          key={item.key}
                          className={`chequeo-item ${
                            valor ? "chequeo-ok" : "chequeo-fail"
                          }`}
                        >
                          {item.label}
                        </span>
                      );
                    })}
                </div>
              </>
            )}

            <div className="ficha-info-footer">
              {container.map((campo, index) => {
                const valor = elemento[campo.key];

                if (!valor || campo.type !== "footer") return null;

                return (
                  <div className="ficha-data" key={campo.key || index}>
                    <strong>{campo.label}</strong>
                    <span>{formatearCampoFirestore(valor).toUpperCase()}</span>
                  </div>
                );
              })}
            </div>

            {/* DÓNDE SE LO VIO, sólo en la ficha de una PERSONA.
             *
             * Es la pregunta que llega por teléfono —«¿dónde está fulano?»— y que
             * hoy se contesta llamando al chofer. Sale de portería, que anota
             * quién cruzó y con qué unidad en el mismo evento.
             *
             * Va sólo en `personas` y no en todas las fichas: en la de un tractor
             * la pregunta ya la contesta «Última ubicación registrada», y repetir
             * lo mismo con otra palabra hace dudar de cuál de las dos mirar. */}
            {/* EL TELÉFONO, cruzando el padrón con la planilla de documentación.
             *
             * El padrón lo tiene en 65 de 2.476 filas; la planilla, en 214 de 224.
             * O sea que el número ya estaba en el hub —se sincroniza solo a las
             * 03:50— y la ficha mostraba un hueco. Son 176 personas que ganan
             * teléfono sin que nadie cargue nada.
             *
             * Va arriba de «Dónde se lo vio» a propósito: son las dos mitades de
             * la misma pregunta, y cuando la ubicación no alcanza para afirmar
             * nada —que es lo más frecuente— la salida es llamarlo. */}
            {coleccion === "personas" && (
              <Telefono dni={elemento.id} delPadron={elemento.telefono} />
            )}

            {coleccion === "personas" && <DondeEsta dni={elemento.id} />}

            {/* QUIÉN TOCÓ ESTA FILA, en la ficha y no sólo en la tabla.
             *
             * La tabla ya lo mostraba en su columna, pero la ficha es donde se
             * mira un dato que parece mal — y ahí es exactamente donde aparece la
             * pregunta. Tenerlo a un «cerrá y fijate en la tabla» de distancia es
             * lo mismo que no tenerlo: la discusión («yo no fui») se arma igual.
             *
             * El hover dice el nombre, el mail y cuándo. El nombre sale CONGELADO
             * de la fila, no del padrón de hoy: la persona pudo haber cambiado de
             * puesto o haberse ido, y ahí es justo cuando el dato hace falta.
             *
             * Sin autor dibuja un guion. Las filas anteriores al sello no tienen
             * quién, y esconder la línea haría que «no se sabe» y «no lo miré» se
             * lean igual. */}
            <div className="ficha-autor">
              <span>Última carga</span>
              <ChipUsuario fila={elemento} size={22} />
            </div>

            {editable && (
              <div className="ficha-buttons">
                <TextButton text={"EDITAR"} onClick={() => setEditando(true)} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default memo(Ficha);
